import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Cart } from '@getjust/gateway';

import { useCountryCodeAtom, useEmailAtom, useFormMessageAtom } from '$hooks/state';
import { CART_QUERY_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdUpdater } from '$src/hooks/client/useCartIdUpdater';
import { useValidateEmail } from '$src/hooks/mutations/useValidateEmail';
import { useCorrectionsAtom } from '$src/hooks/state/useCorrectionsAtom';
import { useCountryCodeContextFromLocalStorage } from '$src/hooks/state/useCountryCodeContextFromLocalStorage';
import { gateway } from '$src/http';
import { UpdateEmailInput } from '$src/pages/api/[shopId]/[cartId]/update-email';

const MUTATION_UPDATE_EMAIL_KEY = 'MUTATION/UPDATE_EMAIL';

export const useUpdateEmail = () => {
  const { initCorrections } = useCorrectionsAtom();
  const baseUrl = useBaseApiUrl();
  const { onEmailChange } = useEmailAtom();
  const { validateEmail } = useValidateEmail();
  const { formMessage } = useFormMessageAtom();
  const { country } = useCountryCodeAtom();
  const countryCodeContext = useCountryCodeContextFromLocalStorage() ?? country;
  const updateCartId = useCartIdUpdater();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEmailInput) => {
      return gateway.post<Cart>(`${baseUrl}/update-email`, {
        email: input.email,
        countryCodeContext: countryCodeContext,
      });
    },
    mutationKey: [MUTATION_UPDATE_EMAIL_KEY, baseUrl],
    onSuccess: async (data, payload) => {
      updateCartId(data.data?.id);
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, data?.data.id], data?.data);

      if (data.data.corrections?.length) {
        initCorrections(data.data.corrections);
      }
      const email =
        data?.data?.customer?.email && data.data.customer.email !== ''
          ? data.data.customer.email
          : payload.email;

      // NOTE: If the user has a Prestashop account AND is not connected to its account
      if (data?.data?.customer?.hasAccount && email && formMessage?.currentUser?.email !== email) {
        await validateEmail(email);
      }

      onEmailChange(email);
    },
  });
};
